<template>
    <div v-click-outside="handleClose" class="search-bar">
        <div
            ref="searchBarContainer"
            class="search-bar__input-container"
            data-bot="searchbar"
            @click="handleOpen"
            @mouseenter="handleShowTooltip"
            @mouseleave="handleHideTooltip"
        >
            <CompassIcon icon="search" />
            <input
                class="search-bar__input"
                type="search"
                :placeholder="$t.__('common.actions.search')"
                @focus="handleOpen"
            />
        </div>

        <transition name="slide-up">
            <div v-if="showTooltip" class="search-bar__tooltip">
                <span class="typographies-paragraph-small-regular">
                    {{ $l10n.__('Rechercher') }}
                </span>
                <span class="shortcut">{{ shortcut }}</span>
            </div>
        </transition>

        <transition name="slide-right">
            <div
                v-if="isOpen"
                class="search-results-container"
                :style="{
                    left: `${resultsContainerStyles.left}px`,
                    width: `${resultsContainerStyles.width}px`,
                }"
            >
                <div class="search-results-container__input-container">
                    <CompassIcon icon="search" />
                    <CompassTagInteractive
                        v-if="showCompanyScan"
                        variant="removable"
                        color="light-gray"
                        modelValue="Annuaire des sociétés"
                        :valueLabel="$l10n.__('Annuaire des sociétés')"
                        @tag-remove="handleRemoveCompanyScan"
                    />
                    <input
                        ref="searchInput"
                        v-model="query"
                        class="search-results-container__input"
                        :placeholder="$t.__('common.actions.search')"
                        type="search"
                        @input="handleSearchQueryInput"
                    />
                    <CompassIconButton
                        v-if="query.length > 0"
                        icon="close"
                        class="search-results-container__input-close"
                        @click.stop="resetSearchResults"
                    />
                </div>

                <div ref="resultsWrapper" class="search-results-container__wrapper">
                    <div v-if="!showCompanyScan" class="search-results-container__results">
                        <SearchBarFilters
                            :currentFilter="currentFilter"
                            :permissions="permissions"
                            @change="handleFilterUpdate"
                        />
                    </div>

                    <div
                        v-if="showLabelRecentlyOpened"
                        class="search-results-container__recently-opened-items typographies-paragraph-small-bold"
                    >
                        {{ $l10n.__('Récemment ouverts') }}
                    </div>

                    <div v-if="isLoading">
                        <div
                            v-for="index in 3"
                            :key="index"
                            class="search-results-container__loader typographies-paragraph-medium-regular"
                        >
                            <CompassSkeleton
                                variant="square"
                                class="search-results-container__loader-image"
                            />
                            <CompassSkeleton
                                :lines="2"
                                class="search-results-container__loader-text"
                            />
                        </div>
                    </div>

                    <div v-if="showNoResultsMessage" class="search-results-container__no-results">
                        <CompassIcon size="small" icon="search-no-result" />
                        <p class="typographies-paragraph-medium-regular">
                            {{
                                $l10n.__('Aucun résultat ne semble correspondre à votre recherche.')
                            }}
                        </p>
                    </div>

                    <div
                        v-if="searchResultsTotal > 0 && !isSimilarResults && !isLoading"
                        class="search-results-container__totalCount typographies-paragraph-medium-regular"
                    >
                        {{
                            $l10n.n__(
                                '%d résultat',
                                '%d résultats',
                                searchResultsTotal,
                                searchResultsTotal,
                            )
                        }}
                    </div>

                    <div
                        v-if="showLabelSimilarResults"
                        class="search-results-container__results-similar typographies-paragraph-medium-regular"
                    >
                        {{ $l10n.__('Vous cherchiez peut-être...') }}
                    </div>

                    <div ref="resultsList" class="search-results-container__results-wrapper">
                        <ResultsRecentlyOpened
                            v-if="
                                isRecentlyOpenedItems &&
                                recentlyOpenedResults.length > 0 &&
                                !query &&
                                currentFilter.length === 0
                            "
                            :results="recentlyOpenedResults"
                        />

                        <SearchResultsCategory
                            v-if="searchResults.companies.count > 0"
                            :results="searchResults.companies"
                            :isFiltered="filtersAreSame()"
                            :isSimilarResults="isSimilarResults"
                            @see-all="handleFilterUpdate"
                        />
                        <SearchResultsCategory
                            v-if="searchResults.individuals.count > 0"
                            :results="searchResults.individuals"
                            :isFiltered="filtersAreSame()"
                            :isSimilarResults="isSimilarResults"
                            @see-all="handleFilterUpdate"
                        />
                        <SearchResultsCategory
                            v-if="searchResults.contacts.count > 0"
                            :results="searchResults.contacts"
                            :isFiltered="filtersAreSame()"
                            :isSimilarResults="isSimilarResults"
                            @see-all="handleFilterUpdate"
                        />
                        <SearchResultsCategory
                            v-if="searchResults.opportunities.count > 0"
                            :results="searchResults.opportunities"
                            :isFiltered="filtersAreSame()"
                            :isSimilarResults="isSimilarResults"
                            @see-all="handleFilterUpdate"
                        />
                        <SearchResultsCategory
                            v-if="hasDocumentResults"
                            :results="documentsData"
                            :groupBy="searchResults.documents.groupBy"
                            :groupedResults="groupedDocumentsData"
                            :minResults="minResults"
                            :isFiltered="filtersAreSame()"
                            :isSimilarResults="isSimilarResults"
                            @see-all="handleFilterUpdate"
                        />
                        <SearchResultsCategory
                            v-if="searchResults.items.count > 0"
                            :results="searchResults.items"
                            :isFiltered="filtersAreSame()"
                            :isSimilarResults="isSimilarResults"
                            @see-all="handleFilterUpdate"
                        />
                        <SearchResultsCategory
                            v-if="searchResults.tickets.count > 0"
                            :results="searchResults.tickets"
                            :isFiltered="filtersAreSame()"
                            :isSimilarResults="isSimilarResults"
                            @see-all="handleFilterUpdate"
                        />
                        <SearchResultsCategory
                            v-if="searchResults.archived.count > 0 && currentFilter.length === 0"
                            :results="searchResults.archived"
                        />
                        <SearchResultsCompanyScan
                            v-for="result in companyScanResults"
                            :key="result.id"
                            :name="result.name"
                            :address="result.address"
                            :siret="result.siret"
                            :uuid="result.uuid"
                            @createCompany="handleCreateCompany"
                        />
                    </div>

                    <div
                        v-if="displayCompanyScanButton"
                        class="search-results-container__company-scan-launch"
                    >
                        <h4
                            class="search-results-container__company-scan-launch-title typographies-paragraph-small-bold"
                        >
                            {{ $l10n.__('Aller plus loin...') }}
                        </h4>
                        <CompassButton
                            :label="$t.__('searchbar.SearchBar.search_in_company_directory')"
                            type="secondary"
                            size="small"
                            data-bot="searchbar.company-scan-launch"
                            @click.stop="handleCompanyScan"
                        />
                    </div>

                    <div class="search-results-container__footer">
                        <div class="search-results-container__footer-help">
                            <!-- <p class="typographies-paragraph-small-regular">
                                {{ $l10n.__('Une remarque sur les résultats obtenus ?') }}
                            </p> -->
                        </div>
                        <ul class="search-results-container__footer-actions">
                            <li class="typographies-paragraph-small-regular">
                                <span>{{ $l10n.__('Naviguer') }}</span>
                                <div class="shortcut">
                                    <CompassIcon size="extra-small" icon="caret-down-fill" />
                                </div>
                                <div class="shortcut">
                                    <CompassIcon size="extra-small" icon="caret-up-fill" />
                                </div>
                            </li>
                            <li class="typographies-paragraph-small-regular">
                                <span>{{ $l10n.__('Ouvrir') }}</span>
                                <div class="shortcut">
                                    <CompassIcon size="extra-small" icon="keyboard-enter" />
                                </div>
                            </li>
                            <li class="typographies-paragraph-small-regular">
                                <span>{{ $l10n.__('Fermer') }}</span>
                                <div class="shortcut">
                                    <CompassIcon size="extra-small" icon="keyboard-esc" />
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </transition>
        <SearchBarCompanyCreateDialog
            v-if="showCompanyCreateDialog"
            :companyToCreate="companyToCreate"
            :countryCode="countryCode"
            @close="handleCloseCompanyCreateDialog"
        />
    </div>
</template>
<script>
import { isMacOS } from 'Helpers/userAgentHelper';
import { normalizeKeyName } from 'Helpers/normalizeKeyHelper';
import SearchBarFilters from './SearchBarFilters.vue';
import { search, companyScan } from 'api/searchEngineService';
import SearchResultsCategory from './SearchResultsCategory.vue';
import ResultsRecentlyOpened from './ResultsRecentlyOpened.vue';
import CompanyAdapter from 'Adapters/SearchEngineBeta/CompanyAdapter';
import ContactAdapter from 'Adapters/SearchEngineBeta/ContactAdapter';
import IndividualAdapter from 'Adapters/SearchEngineBeta/IndividualAdapter';
import OpportunityAdapter from 'Adapters/SearchEngineBeta/OpportunityAdapter';
import ProductAdapter from 'Adapters/SearchEngineBeta/ProductAdapter';
import DocumentAdapter from 'Adapters/SearchEngineBeta/DocumentAdapter';
import RedactorAdapter from 'Adapters/SearchEngineBeta/RedactorAdapter';
import SupportTicketAdapter from 'Adapters/SearchEngineBeta/SupportTicketAdapter';
import ArchivedAdapter from 'Adapters/SearchEngineBeta/ArchivedAdapter';
import { createNamespacedHelpers } from 'vuex';
import SearchResultsCompanyScan from './results/SearchResultsCompanyScan.vue';
import { v4 as uuidv4 } from 'uuid';
import SearchBarCompanyCreateDialog from './SearchBarCompanyCreateDialog.vue';
import SessionStorage from 'Core/commons/SessionStorage';
import { nextTick } from 'vue';

const user = createNamespacedHelpers('user');

export default {
    components: {
        SearchBarFilters,
        SearchResultsCategory,
        SearchResultsCompanyScan,
        ResultsRecentlyOpened,
        SearchBarCompanyCreateDialog,
    },
    provide() {
        return {
            searchbar: this,
        };
    },
    data() {
        return {
            isOpen: false,
            showTooltip: false,
            resultsContainerStyles: {
                left: 0,
                width: 0,
            },
            query: '',
            currentFilter: [],
            isLoading: false,
            searchResultsTotal: 0,
            searchResults: {
                companies: [],
                contacts: [],
                opportunities: [],
                documents: [],
                redactor: [],
                items: [],
                tickets: [],
                individuals: [],
                archived: [],
            },
            recentlyOpenedResults: [],
            flatSearchResults: [],
            searchResultsAsComponents: [],
            hoverIndex: -1,
            showNoResults: false,
            minResults: 3,
            isSimilarResults: false,
            showCompanyScan: false,
            companyScanResults: [],
            isRecentlyOpenedItems: true,
            showCompanyCreateDialog: false,
            companyToCreate: {},
            countryCode: 'FR',
            controller: new AbortController(),
        };
    },
    computed: {
        ...user.mapState({
            locale: (state) => state.locale,
            permissions: (state) => state.permissions.searchBarFilter,
        }),
        showNoResultsMessage() {
            return (
                (this.showNoResults || this.isSimilarResults) &&
                !this.isLoading &&
                this.companyScanResults.length === 0
            );
        },
        getFlatSearchResults() {
            return this.flatSearchResults;
        },
        shortcut() {
            return `${normalizeKeyName('Ctrl')} + K`;
        },
        hasDocumentResults() {
            return (
                (this.searchResults.documents &&
                    this.searchResults.documents.items &&
                    this.searchResults.documents.count > 0) ||
                (this.searchResults.redactor &&
                    this.searchResults.redactor.items &&
                    this.searchResults.redactor.count > 0)
            );
        },
        documentsData() {
            return {
                ...this.searchResults.documents,
                items: [
                    ...this.searchResults.documents.items,
                    ...this.searchResults.redactor.items,
                ],
                count: this.searchResults.documents.count + this.searchResults.redactor.count,
            };
        },
        groupedDocumentsData() {
            const output = {};
            for (const group of this.searchResults.documents.groupBy) {
                output[group.key] = this.groupedResults(group.key, this.documentsData.items);
            }
            return output;
        },
        flattenedDocumentsData() {
            const output = [];
            for (const group of this.searchResults.documents.groupBy) {
                output.push(...this.groupedDocumentsData[group.key]);
            }
            return output;
        },
        showLabelRecentlyOpened() {
            return this.isRecentlyOpenedItems && !this.query && this.currentFilter.length === 0;
        },
        showLabelSimilarResults() {
            return this.isSimilarResults && this.searchResultsTotal > 0 && !this.isLoading;
        },
        displayCompanyScanButton() {
            return (
                (this.showNoResults || this.isSimilarResults) &&
                !this.showCompanyScan &&
                !this.isLoading &&
                (this.currentFilter.length === 0 || this.currentFilter.includes('company'))
            );
        },
    },
    watch: {
        isOpen(newValue) {
            if (newValue) {
                nextTick(() => {
                    this.$refs.resultsWrapper.style.maxHeight = `${this.$refs.resultsWrapper.scrollHeight}px`;
                    this.$refs.resultsWrapper.ontransitionend = () => {
                        if (this.$refs.resultsWrapper) {
                            this.$refs.resultsWrapper.style.maxHeight = 'fit-content';
                        }
                    };
                });
            }
        },
    },
    created() {
        window.addEventListener('resize', this.resizeScreen);
        window.addEventListener('keydown', this.searchBarKeyboardEvents);

        const storage = new SessionStorage('searchbar', 'filter');
        if (storage.get()) {
            this.currentFilter = storage.get();
        }
    },
    mounted() {
        this.getResultsContainerStyles();
    },
    onBeforeDestroy() {
        window.removeEventListener('resize', this.resizeScreen);
        window.removeEventListener('keydown', this.searchBarKeyboardEvents);
    },
    methods: {
        resizeScreen: _.debounce(function () {
            this.getResultsContainerStyles();
        }, 200),
        searchBarKeyboardEvents(event) {
            if (
                (event.key === 'k' || event.key === 'K') &&
                (event.ctrlKey || (isMacOS() && event.metaKey))
            ) {
                event.preventDefault();
                if (window.pendo?.accountId) {
                    window.pendo.track('searchbar.opened');
                }
                this.handleOpen();
            }

            if (event.key === 'Escape') {
                this.handleClose();
            }

            if (event.key === 'Enter') {
                this.handleResultSelect();
            }

            if (event.key === 'ArrowDown') {
                this.handleKeyboardNavigation('next');
            }

            if (event.key === 'ArrowUp') {
                this.handleKeyboardNavigation('prev');
            }
        },
        handleOpen() {
            this.getResultsContainerStyles();
            this.showTooltip = false;
            this.isOpen = true;
            nextTick(() => {
                this.$refs.searchInput.focus();
            });
            if (!this.currentFilter.length) {
                this.getRecentlyOpenedItems();
            }
        },
        resetSearchResults(keepFilter = false) {
            this.query = '';
            for (const key in this.searchResults) {
                this.searchResults[key] = [];
            }
            this.searchResultsTotal = 0;
            if (!keepFilter) {
                this.currentFilter = [];
            }
            this.flatSearchResults = [];
            this.searchResultsAsComponents = [];
            this.showNoResults = false;
            this.isSimilarResults = false;
            this.showCompanyScan = false;
            this.companyScanResults = [];
            this.isRecentlyOpenedItems = true;
        },
        handleClose() {
            if (this.isOpen) {
                this.resetSearchResults(true);
            }
            this.isOpen = false;
        },
        getResultsContainerStyles() {
            const left = this.$refs.searchBarContainer.getBoundingClientRect().left;
            const nav = document.querySelector('.navbar-content__actions');
            const right = nav ? nav.getBoundingClientRect().right : 0;
            this.resultsContainerStyles.left = left;
            this.resultsContainerStyles.width = right - left;
        },
        handleShowTooltip() {
            this.showTooltip = true;
        },
        handleHideTooltip() {
            this.showTooltip = false;
        },
        handleFilterUpdate(filter) {
            this.currentFilter = filter;
            if (this.query.length > 0) {
                for (const key in this.searchResults) {
                    this.searchResults[key] = [];
                }
                this.searchResultsTotal = 0;
                this.isLoading = true;
                this.handleSearchQueryInput(true);
            } else if (this.currentFilter.length === 0) {
                this.getRecentlyOpenedItems();
            }
            const storage = new SessionStorage('searchbar', 'filter');
            storage.set(filter);
        },
        getRecentlyOpenedItems() {
            this.isRecentlyOpenedItems = true;
            this.recentlyOpenedResults = [];
            this.isLoading = true;
            ApiClient.get('/spotlight-objects', {})
                .then((res) => {
                    for (const item of res.data) {
                        const type = item.object.type;
                        let adaptedItem;

                        switch (type) {
                            case 'company':
                                adaptedItem = new CompanyAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            case 'individual':
                                adaptedItem = new IndividualAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            case 'contact':
                                adaptedItem = new ContactAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            case 'opportunity':
                                adaptedItem = new OpportunityAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            case 'document':
                            case 'purchase':
                                adaptedItem = new DocumentAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            case 'redactor':
                                adaptedItem = new RedactorAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            case 'item':
                            case 'declination':
                                adaptedItem = new ProductAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            case 'ticket':
                                adaptedItem = new SupportTicketAdapter(item).build(true);
                                this.recentlyOpenedResults.push(adaptedItem);
                                break;
                            default:
                                break;
                        }
                    }
                    if (this.recentlyOpenedResults.length === 0) {
                        this.isRecentlyOpenedItems = false;
                    }
                    this.isLoading = false;
                })
                .catch((error) => {
                    this.isLoading = false;
                    Push.error(error.message);
                });
        },
        handleSearchQueryInput: _.debounce(function (prefiltered = false) {
            if (this.query.length > 0) {
                this.controller.abort();
                this.controller = new AbortController();
                this.isLoading = true;
                this.flatSearchResults = [];
                this.isRecentlyOpenedItems = false;
                for (const key in this.searchResults) {
                    this.searchResults[key] = [];
                }
                this.searchResultsAsComponents = [];

                if (this.showCompanyScan) {
                    this.flatSearchResults = [];
                    for (const key in this.searchResults) {
                        this.searchResults[key] = [];
                    }
                    this.searchResultsAsComponents = [];
                    this.companyScanResults = [];
                    this.handleCompanyScan();
                    return;
                }

                if (this.currentFilter.length > 0 && window.pendo?.accountId) {
                    window.pendo.track('searchbar.filter', {
                        source: prefiltered ? 'prefilter' : 'filter',
                        category: this.currentFilter.join(', '),
                    });
                }

                search({
                    query: this.query,
                    type: this.currentFilter,
                    signal: this.controller.signal,
                })
                    .then((res) => {
                        this.isRecentlyOpenedItems = false;
                        this.isSimilarResults = res.is_similar_results;
                        this.searchResultsTotal =
                            this.currentFilter.length > 0 &&
                            Object.values(res.data.aggregations.count_by_type).length > 0
                                ? Object.values(res.data.aggregations.count_by_type)[0][0]
                                : res.data.pagination.total;

                        this.showNoResults = res.data.pagination.total === 0;

                        this.searchResults.companies = new CompanyAdapter(
                            res.data,
                            this.query,
                        ).build();
                        this.searchResults.individuals = new IndividualAdapter(
                            res.data,
                            this.query,
                        ).build();
                        this.searchResults.contacts = new ContactAdapter(
                            res.data,
                            this.query,
                        ).build();
                        this.searchResults.opportunities = new OpportunityAdapter(
                            res.data,
                            this.query,
                        ).build();
                        this.searchResults.documents = new DocumentAdapter(
                            res.data,
                            this.query,
                        ).build();
                        this.searchResults.redactor = new RedactorAdapter(res.data).build();
                        this.searchResults.items = new ProductAdapter(res.data, this.query).build();
                        this.searchResults.tickets = new SupportTicketAdapter(
                            res.data,
                            this.query,
                        ).build();
                        this.searchResults.archived = new ArchivedAdapter(
                            res.data,
                            this.query,
                        ).build();

                        // To have the search results in the correct order
                        this.flatSearchResults = [
                            ...this.searchResults.companies.items,
                            ...this.searchResults.individuals.items,
                            ...this.searchResults.contacts.items,
                            ...this.searchResults.opportunities.items,
                            ...this.flattenedDocumentsData,
                            ...this.searchResults.items.items,
                            ...this.searchResults.tickets.items,
                            ...this.searchResults.archived.items,
                        ];
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') return;
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: this.$l10n.__(
                                'Un problème est survenu lors de la recherche. Veuillez réessayer plus tard.',
                            ),
                            close: true,
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else {
                if (!this.showCompanyScan) {
                    this.isLoading = false;
                    this.controller.abort();
                    this.resetSearchResults(true);
                }
            }
        }, 300),
        filtersAreSame() {
            return JSON.stringify(this.currentFilter) !== '[]';
        },
        scrollToBeVisible(element, container) {
            const elementTop = element.offsetTop;
            const elementBottom = elementTop + element.clientHeight;

            const containerTop = container.scrollTop + container.offsetTop;
            const containerBottom = containerTop + container.clientHeight;

            if (elementTop < containerTop || elementBottom > containerBottom) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            }
        },
        handleKeyboardNavigation(direction) {
            if (this.searchResultsTotal === 0 || !this.isOpen) return;

            if (direction === 'next') {
                if (this.hoverIndex < this.flatSearchResults.length - 1) {
                    this.hoverIndex++;
                }
            } else if (direction === 'prev') {
                if (this.hoverIndex > 0) {
                    this.hoverIndex--;
                }
            }

            let selectedOption;

            for (const [index, item] of this.flatSearchResults.entries()) {
                if (index === this.hoverIndex) {
                    this.searchResultsAsComponents.map((result) => {
                        if (result.uuid === item.uuid) {
                            result.isHovered = true;
                            selectedOption = result;
                        } else {
                            result.isHovered = false;
                        }
                        return result;
                    });
                }
            }

            const container = this.$refs.resultsList;

            if (selectedOption) {
                this.scrollToBeVisible(selectedOption.$el, container);
            }
        },
        handleResultSelect() {
            if (this.hoverIndex > -1 && this.flatSearchResults[this.hoverIndex]) {
                if (this.flatSearchResults[this.hoverIndex].link) {
                    window.location.href = this.flatSearchResults[this.hoverIndex].link;
                } else {
                    const selectedResult = this.searchResultsAsComponents.find(
                        (item) => item.uuid === this.flatSearchResults[this.hoverIndex].uuid,
                    );
                    selectedResult.handleSelect();
                }
            }
        },
        groupedResults(key, items) {
            if (this.currentFilter.length) return this.groupResults(key, items); // if all results are displayed

            return this.reduceResults(this.groupResults(key, items));
        },
        groupResults(key, items) {
            return items.filter((item) => item.type === key);
        },
        reduceResults(data) {
            return data.slice(0, this.minResults).map((i) => {
                return i;
            });
        },
        handleCompanyScan() {
            this.isLoading = true;
            this.showCompanyScan = true;

            this.countryCode = this.locale.match('fr') ? 'FR' : 'GB';

            this.flatSearchResults = [];
            for (const key in this.searchResults) {
                this.searchResults[key] = [];
            }
            this.searchResultsAsComponents = [];

            companyScan(this.query, this.countryCode)
                .then((res) => {
                    this.searchResultsTotal = res.data.pagination.total;
                    this.showNoResults = res.data.pagination.total === 0;
                    this.isSimilarResults = false;

                    this.companyScanResults = res.data.data.map((item) => {
                        return {
                            ...item,
                            uuid: uuidv4(),
                        };
                    });

                    this.flatSearchResults = [...this.companyScanResults];
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__(
                            "Un problème est survenu lors de la recherche dans l'annuaire des sociétés. Veuillez réessayer plus tard.",
                        ),
                        close: true,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        handleRemoveCompanyScan() {
            this.showCompanyScan = false;
            this.companyScanResults = [];
            this.handleSearchQueryInput();
        },
        handleCreateCompany(company) {
            this.companyToCreate = { ...company, siret: company.siret || '' };
            this.showCompanyCreateDialog = true;
        },
        handleCloseCompanyCreateDialog() {
            this.companyToCreate = {};
            this.showCompanyCreateDialog = false;
        },
    },
};
</script>
<style lang="scss" scoped>
.search-bar {
    align-items: center;
    display: flex;
    flex: 1;
    order: 1;
    justify-content: center;
    position: relative;

    &__tooltip {
        align-items: center;
        background: var(--colors-common-background-default-contrast, #111);
        border-radius: var(--xx-small, 4px);
        color: var(--colors-common-text-standard-on-color, #fff);
        display: flex;
        gap: var(--spacing-0);
        justify-content: center;
        left: 50%;
        padding: var(--spacing-0) var(--spacing-1);
        position: absolute;
        transform: translate3d(-50%, 4px, 0);
        top: 100%;

        .shortcut {
            background: var(--colors-common-background-darker-hover, #565665);
            border-radius: var(--xx-small, 4px);
            font-family: var(--font-families-lato);
            font-size: 10px;
            font-style: normal;
            font-weight: 400;
            line-height: 12px;
            padding: var(--spacing-0);
        }
    }

    &__input-container {
        align-items: center;
        background: var(--colors-component-background-illustration-brand-secondary-grey);
        border-radius: 18px;
        cursor: pointer;
        display: flex;
        flex: 1;
        gap: var(--spacing-1);
        max-width: 1040px;
        padding: var(--spacing-0) var(--spacing-2);
        transition: all 300ms ease-in-out;

        &:hover {
            background: var(--colors-common-background-default, #fff);
            box-shadow: inset 0 0 0 2px var(--colors-common-border-focus, #4868fd);
        }

        :deep(.compass-icon) {
            color: var(--colors-common-text-subtle-default);
        }
    }

    &__input {
        background: transparent;
        border: none;
        color: var(--colors-common-text-subtle-default);
        cursor: pointer;
        flex: 1;
        font-family: var(--font-families-lato);
        font-size: 14px;
        font-weight: 400;
        height: 28px;
        line-height: 20px;
        outline: none;
        padding: 0;

        &::placeholder {
            color: var(--colors-common-text-subtle-default);
        }
    }
}

.search-results-container {
    background: var(--colors-common-background-default, #fff);
    border-radius: 8px;
    box-shadow: 0px 0px 12px 0px rgba(17, 17, 17, 0.24);
    overflow: hidden;
    position: fixed;
    top: 12px;
    z-index: 10;

    @media screen and (min-width: 1940px) {
        max-width: 1040px;
    }

    &__input-container {
        align-items: center;
        border-bottom: 1px solid var(--colors-common-border-lighter, #e9e9ec);
        display: flex;
        gap: var(--spacing-1);
        height: 56px;
        padding: 0 var(--spacing-3);

        :deep() {
            .compass-icon {
                color: var(--colors-common-text-subtle-default);
            }
            .search-results-container__input-close .compass-icon {
                color: var(--colors-common-text-standard-default);
            }
        }
    }

    &__input {
        background: transparent;
        border: none;
        color: var(--colors-common-text-subtle-default);
        flex: 1;
        font-family: var(--font-families-lato);
        font-size: 14px;
        font-weight: 400;
        height: 100%;
        line-height: 20px;
        outline: none;
        padding: 0;
    }

    &__loader {
        color: var(--colors-common-text-standard-default, #111);
        display: flex;
        gap: 10px;
        padding: var(--spacing-2) var(--spacing-3);

        &-text {
            width: 100%;
            max-width: 400px;
        }
    }

    &__results {
        padding: var(--spacing-3);

        &-wrapper {
            max-height: 480px;
            overflow-y: auto;

            @media screen and (max-height: 768px) {
                max-height: 55vh;
            }
        }

        &-similar {
            color: var(--colors-common-text-subtle-default, #565665);
            padding: var(--spacing-2) var(--spacing-3);
        }
    }

    &__recently-opened-items {
        color: var(--colors-common-text-subtle-default, #565665);
        padding: var(--spacing-3) var(--spacing-3) var(--spacing-0);
    }

    &__no-results {
        align-items: center;
        background: var(--colors-common-background-primary-subtle);
        border-radius: var(--spacing-0);
        color: var(--colors-common-text-standard-default, #111);
        display: flex;
        gap: var(--spacing-0);
        margin: 0 var(--spacing-3) var(--spacing-3);
        padding: var(--spacing-1) var(--spacing-4);

        p {
            margin: 0;
        }
    }

    &__totalCount {
        color: var(--colors-common-text-standard-default, #111);
        padding: var(--spacing-2) var(--spacing-3);
    }

    &__wrapper {
        max-height: 0;
        overflow: hidden;
        transition: all 400ms ease-in-out 200ms;
    }

    &__company-scan-launch {
        padding: var(--spacing-3);

        &-title {
            color: var(--colors-common-text-subtle-default, #565665);
            margin: 0 0 var(--spacing-0);
        }
    }

    &__footer {
        align-items: center;
        border-top: 1px solid var(--colors-common-border-lighter, #e9e9ec);
        display: flex;
        justify-content: space-between;
        padding: var(--spacing-1);

        &-help {
            color: var(--colors-common-text-subtle-default, #565665);

            p {
                margin: 0;
            }
        }

        &-actions {
            align-items: center;
            color: var(--colors-common-text-subtle-default, #565665);
            display: flex;
            gap: var(--spacing-1);
            margin: 0;

            li {
                align-items: center;
                display: flex;
                gap: var(--spacing-0);

                .shortcut {
                    align-items: center;
                    background: var(--colors-common-background-subtle, #f6f6f9);
                    border-radius: 4px;
                    color: var(--colors-common-text-subtle-default);
                    display: flex;
                    padding: var(--spacing-0);
                }
            }
        }
    }
}

.slide-up-enter-active {
    animation: slide-up 200ms;
}
@keyframes slide-up {
    0% {
        opacity: 0;
        transform: translate3d(-50%, 12px, 0);
    }
    100% {
        opacity: 1;
        transform: translate3d(-50%, 4px, 0);
    }
}

.slide-right-enter-active {
    animation: slide-right 200ms;
}
@keyframes slide-right {
    0% {
        opacity: 0;
        transform: translateX(-20px);
    }
    100% {
        opacity: 1;
        transform: translateX(0);
    }
}
</style>
